import { some } from 'lodash-es'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { matchPath, useHistory } from 'react-router-dom'

import { resetEventTypeEditorSidebar, scopes } from 'features/event_type_editor'
import { useRightRail } from 'features/right_rail'

import { initializeSingleUseLinkSidebar, resetSingleUseLinkSidebar } from './helpers'
import changeSet from '../../core/change_set'

export const useSingleUseLinkSidebar = () => {
  const dispatch = useDispatch()
  const { openOrReplace, close, current, replace } = useRightRail()

  const isCreationFlow = useMemo(() => (
    ['true', true].includes(current?.paneProps?.isCreation)
  ), [current])

  const openSidebar = useCallback(async ({
    singleUseLinkAttrs = {},
    selectedEventTypeAttrs = {},
    isCreation = false
  }) => {
    if (current?.pane === 'event_type_editor') {
      close({ rail: { name: 'event_type_editor', state: current.paneState } })
      await dispatch(resetEventTypeEditorSidebar())
    }

    openOrReplace('single_use_link', { ...singleUseLinkAttrs, ...selectedEventTypeAttrs, isCreation })
    dispatch(initializeSingleUseLinkSidebar())
  }, [close, current, dispatch, openOrReplace])

  const closeSidebar = useCallback(async ({ resetAnalytics = true, resetAlert = false } = {}) => {
    // CRITICAL: Clear the right rail stack SYNCHRONOUSLY first to prevent useUrlSync race condition
    close({ rail: { name: 'single_use_link', state: current.paneState } })
    // Then handle the cleanup
    dispatch(resetSingleUseLinkSidebar({ resetAnalytics, resetAlert }))
  }, [current, dispatch, close])

  const replaceSidebar = useCallback(async (paneProps) => {
    await dispatch(resetSingleUseLinkSidebar({ resetAnalytics: false }))
    replace('single_use_link', { ...current?.paneProps, ...paneProps })
    dispatch(initializeSingleUseLinkSidebar())
  }, [dispatch, replace, current])

  return {
    openSidebar,
    closeSidebar,
    replaceSidebar,
    paneState: current?.paneProps,
    isCreation: isCreationFlow,
    isOpen: current?.pane === 'single_use_link',
  }
}

const getSulSidebarPaths = () => [
  '/event_types',
  '/app/scheduling/meeting_types',
  '/app/scheduling/single_use_links'
].flatMap((path) => scopes.map((scope) => ([path, scope].join('/'))))

export const useCloseOnNavigation = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { clearStack } = useRightRail()

  useEffect(() => {
    const unblock = history.block((location) => {
      const isNotOnProperPage = !some(getSulSidebarPaths(), (path) => matchPath(location.pathname, path))

      if (isNotOnProperPage) {
        if (!changeSet.confirmReset()) return false

        // Clear the right rail stack synchronously WITHOUT updating the URL
        // This prevents race conditions where history.replace() interferes with back navigation
        // The useUrlSync hook will properly clean up the URL params after navigation completes
        clearStack()
        dispatch(resetSingleUseLinkSidebar({ resetAnalytics: true, resetAlert: true }))
        unblock()
      }

      return true
    })

    return () => unblock()
  }, [clearStack, dispatch, history])
}
